/** ISO 3166-1 alpha-2 code → display name (common codes for job locations) */
const COUNTRY_NAMES = {
  IN: "India", NG: "Nigeria", PK: "Pakistan", IR: "Iran", AM: "Armenia",
  BR: "Brazil", VE: "Venezuela", HT: "Haiti", US: "United States", GB: "United Kingdom",
  CA: "Canada", AU: "Australia", DE: "Germany", FR: "France", ES: "Spain",
  MX: "Mexico", CO: "Colombia", AR: "Argentina", PH: "Philippines", ID: "Indonesia",
  BD: "Bangladesh", EG: "Egypt", KE: "Kenya", ZA: "South Africa", UA: "Ukraine",
  PL: "Poland", RO: "Romania", NL: "Netherlands", IT: "Italy", CN: "China",
  JP: "Japan", KR: "South Korea", SG: "Singapore", MY: "Malaysia", TH: "Thailand",
};

/**
 * Returns a display string for job location.
 * Uses job.location if set; otherwise shows only countries from the DB (no workType).
 */
export function getJobLocation(job) {
  if (!job) return "";
  if (job.location && String(job.location).trim()) return job.location.trim();
  return getCountriesDisplay(job);
}

/**
 * Returns a display string from job.countries only (no job.location).
 * Use for inline "workType - countries" display.
 */
export function getCountriesDisplay(job) {
  if (!job) return "";
  const codes = Array.isArray(job.countries) ? job.countries : [];
  const countryStr = codes
    .map((c) => COUNTRY_NAMES[c] || c)
    .filter(Boolean)
    .join(", ");
  return countryStr || "";
}

/**
 * Returns display config for job salary based on payType from DB.
 * Monthly → monthly salary; Hourly → hourly rate; Yearly → annual.
 */
export function getSalaryDisplay(job) {
  if (!job?.salaryRange || job.salaryRange.min == null || job.salaryRange.max == null) {
    return null;
  }
  const payType = (job.payType || "Monthly").toString();
  const { min, max, currency } = job.salaryRange;
  const c = currency || "USD";

  if (payType === "Hourly") {
    return {
      label: "Hourly Rate",
      amountText: `$${Number(min).toLocaleString()} - $${Number(max).toLocaleString()}`,
      unit: `/${c}/hour`,
    };
  }
  if (payType === "Yearly") {
    return {
      label: "Annual Salary",
      amountText: `$${Number(min).toLocaleString()} - $${Number(max).toLocaleString()}`,
      unit: `/${c}/year`,
    };
  }
  // Monthly (default)
  return {
    label: "Monthly Salary",
    amountText: `$${Number(min).toLocaleString()} - $${Number(max).toLocaleString()}`,
    unit: `/${c}/month`,
  };
}

/**
 * Returns display info for timezone: long name (e.g. "Central") and UTC offset (e.g. "UTC−5").
 * Uses Intl API; returns null if timezone is invalid or unsupported.
 */
export function getTimezoneDisplay(tz) {
  if (!tz || typeof tz !== "string") return null;
  const timeZone = tz.trim();
  if (!timeZone) return null;
  try {
    const date = new Date();
    const offsetFmt = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "longOffset",
    });
    const offsetParts = offsetFmt.formatToParts(date);
    const gmtPart = offsetParts.find((p) => p.type === "timeZoneName");
    const gmtStr = gmtPart ? gmtPart.value : "";
    const utcOffset = gmtStr.replace(/^GMT/, "UTC").replace("-", "\u2212");

    const nameFmt = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "longGeneric",
    });
    const nameParts = nameFmt.formatToParts(date);
    const namePart = nameParts.find((p) => p.type === "timeZoneName");
    const longName = (namePart && namePart.value) ? namePart.value.replace(/\s*Time\s*$/i, "") : "";

    return { longName, utcOffset };
  } catch (_) {
    return null;
  }
}
